'use client';

import { AwsRum, AwsRumConfig } from 'aws-rum-web';
import { createContext, PropsWithChildren, useEffect, useRef } from 'react';
import { getSessionId } from '@peerless/ui-shared';
import { AwsRumSettings } from './settings';

export interface RumContext {
  current: AwsRum | undefined;
}

export const RumContext = createContext<RumContext>({ current: undefined });

export function AwsRumProviderClient(props: PropsWithChildren<AwsRumSettings>) {
  const rum = useRef<AwsRum | undefined>(undefined);

  useEffect(() => {
    // Initialize the AWS RUM SDK
    // This must be done in a useEffect to avoid running on the server
    rum.current = initializeRum(props);

    // eslint-disable-next-line react-hooks/exhaustive-deps -- only run once
  }, []);

  return <RumContext.Provider value={rum}>{props.children}</RumContext.Provider>;
}

function initializeRum({
  endpoint,
  identityPoolId,
  application,
  sessionSampleRate,
}: AwsRumSettings) {
  const config = {
    endpoint,
    identityPoolId,
    sessionSampleRate,
    telemetries: ['performance', 'errors', 'http'],
    allowCookies: true,
    enableXRay: true,
    sessionAttributes: { correlationId: getSessionId() },
  } satisfies AwsRumConfig;

  try {
    const awsRum = new AwsRum(application.id, application.version, application.region, config);

    return awsRum;
  } catch (error) {
    console.error('Error initializing AWS RUM SDK', error);
    return undefined;
  }
}
